
import { Injectable, WritableSignal, computed, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { GlobalVariable } from '../shared/global.settings';
import { BookList, Book, ISeriesNames, IBook, IBookList } from '../_models';
import { firstValueFrom } from 'rxjs';
import { LogService } from './logging/log.service';
import { TextService } from './text.service';
import { AuthService } from './auth.service';

/**
 * @class BookService
 * @memberof Services
 * @classdesc The BookService class is used to interact with the book API
 * 
 */
@Injectable({
  providedIn: "root"
})
export class BookService {

  http = inject(HttpClient);
  logService = inject(LogService);
  textService = inject(TextService);
  authService = inject(AuthService);

  httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' })
  };

  me = computed(() => this.authService.user()?.username);
  isLoggedIn = computed(() => this.authService.isLoggedIn());

  authHeaders(): HttpHeaders {
    return this.httpOptions.headers.append(
      'Authorization',
      'Bearer ' + this.authService.authtoken()
    );
  }

  private bookApiUrl = GlobalVariable.API_BASE;

  /** 
   * @function getBookList
   * @memberof Services.BookService
   * @description Routine that calls the backend to get a list of books
    * @returns BookList object containing the list of books
   */
  async getBookList(): Promise<BookList> {
    const books$ = this.http.get<IBookList>(`${this.bookApiUrl}/books`);
    const payload = await firstValueFrom(books$);
    return payload;
  }

  /**
   * @function getBookByAsin
   * @memberof Services.BookService
   * @description Routine that calls the backend to get a book by its Amazon ID
   * @param amazon_id of the book to be retrieved
   * @returns Book object containing the details of the book
   * @throws Error if the book could not be found 
   *  
   * 
   */
  async getBookByAsin(asin: string): Promise<Book> {
    const book$ = this.http.get<IBook>(`${this.bookApiUrl}/books/${asin}`);
    return await firstValueFrom(book$) as Book;
  }

  /**
   * @memberof Services.BookService
   * @function getSeries
   * @description Routine that calls the backend to get a list of series
   * @returns An array containing all the series in the database
   * 
   */
  async getSeries(): Promise<ISeriesNames[]> {
    const response = await fetch(`${this.bookApiUrl}/series-names`);
    return await response.json();
  }

  /**
   * @memberof Services.BookService
   * @function getAuthorList
   * @description Routine that calls the backend to get a list of authors
   * @returns An array containing all the authors in the database
   *   
   */
  async getAuthorList(): Promise<{ author: string }[]> {
    const response = await fetch(`${this.bookApiUrl}/author-names`);
    return await response.json();
  }

  /**
   *  @memberof Services.BookService
   * @function getNarratorList
   * @description Routine that calls the backend to get a list of narrators
   * @returns An array containing all the narrators in the database
   * 
   */
  async getNarratorList(): Promise<{ narrator: string }[]> {
    const response = await fetch(`${this.bookApiUrl}/narrator-names`);
    return await response.json();
  }
  /**
   *  @memberof Services.BookService
   * @function getGenreList
   * @description Routine that calls the backend to get a list of genres
   * @returns An array containing all the genres in the database
   */
  async getGenreList(): Promise<string[]> {
    const response = await fetch(`${this.bookApiUrl}/genres`);
    return await response.json();
  }

  // MOCK of data hope to eventually get from App Management v2 - Node version 
  async getUsers(): Promise<{ username: string }[]> {
    return (
      [
        { username: "Amy" },
        { username: "Sarah" },
        { username: "Steve" }
      ]
    );
  }

  /**
   * @memberof Services.BookService
   * @function updateBook
   * @description Routine that calls the backend with the details of a book to be updated
   * Reads the current JSON web token and passes that along with the data
   * @param Partial<IBook> data for the book to be updated
   * @returns IBook object containing the details of the book updated in the database
   * @throws Error if the book could not be updated
   * @throws Error if the book could not be found
   * 
   */
  async updateBook(book: Partial<IBook>): Promise<IBook> {
    const cleanbook = book;
    cleanbook.description = this.textService.encodeText(cleanbook.description!);
    const book$ = this.http
      .put(`${this.bookApiUrl}/books/${book.amazon_id}`, book, {
        headers: this.authHeaders()
      });
    const result = await firstValueFrom(book$) as Book;
    this.logService.info(JSON.stringify(result));
    return result;
  }

  /*
   * @memberof Services.BookService
   * @function addBook
   * @description Routine that calls the backend with the details of a book to be added
   * Reads the current JSON web token and passes that along with the data
   * @param Partial<IBook> data for the book to be added
   * @returns IBook object containing the details of the book added to the database
   * @throws Error if the book could not be added
   */
  async addBook(book: Partial<IBook>): Promise<IBook> {
    const book$ = this.http.post<IBook>(`${this.bookApiUrl}/books`, book, {
      headers: this.authHeaders()
    });
    const result = await firstValueFrom(book$);
    this.logService.info(JSON.stringify(result));
    return result;
  }

  /**
   * @memberof Services.BookService
   * @function deleteBook
   * @description Routine that calls the backend with the amazon_id of a book to be deleted
   * @param amazon_id of the book to be deleted
   * @returns string containing the result of the delete operation
   * @throws Error if the book could not be deleted
   * @throws Error if the book could not be found
   * 
   */
  async deleteBook(bookId: string): Promise<string> {
    const book$ = this.http.delete(`${this.bookApiUrl}/books/${bookId}`, {
      headers: this.authHeaders()
    });
    const result = await firstValueFrom(book$);
    this.logService.info(JSON.stringify(result));
    return JSON.stringify(result);

  }

  getAddedByMe(book: IBook): boolean {
    if (this.isLoggedIn() && book.addedBy == this.me()) {
      return true;
    } else {
      return false;
    }
  }

  async setAddedByMe(book: WritableSignal<Book>, set: boolean): Promise<void> {
    const data = {
      amazon_id: book().amazon_id,
      addedBy: (set ? this.me() : ''),
    }
    if (this.isLoggedIn()) {
      const book$ = this.http
        .put(`${this.bookApiUrl}/books/${book().amazon_id}/added_by`, data, {
          headers: this.authHeaders()
        });
      const result = await firstValueFrom(book$) as Book;
      this.logService.info(JSON.stringify(result));
    };
  }

  /**
   * Given a book, check if the current user has read it
   * @memberof Services.BookService 
   * @notation Requires a logged in user
   * @param book 
   * @returns boolean
   */
  getIHaveRead(book: IBook): boolean {
    if (this.isLoggedIn() && book.hasRead && book.hasRead.includes(this.me()!)) {
      return true;
    } else {
      return false;
    }
  }
  /**
   * Given a book, set the current user as having read it
   * @memberof Services.BookService 
   * @notation Requires a logged in user
   * @param book 
   */
  async setHasRead(book: WritableSignal<Book>, set: boolean): Promise<void> {
    if (this.isLoggedIn()) {
      let currentHasRead = book().hasRead || [];
      if (set) {
        if (!currentHasRead.includes(this.me()!)) {
          currentHasRead.push(this.me()!);
        }
      } else {
        currentHasRead = currentHasRead.filter((reader) => reader !== this.me()!);
      }
      if (currentHasRead.length === 0) {
        currentHasRead = [];
      }
      const data = {
        amazon_id: book().amazon_id,
        hasRead: JSON.stringify(currentHasRead),
      }
      const book$ = this.http
        .put(`${this.bookApiUrl}/books/${book().amazon_id}/has_read`, data, {
          headers: this.authHeaders()
        });
      const result = await firstValueFrom(book$) as Book;
      this.logService.info(JSON.stringify(result));

    };
  }

  getImInterested(book: IBook): boolean {
    if (this.isLoggedIn() && book.isInterested && book.isInterested.includes(this.me()!)) {
      return true;
    } else {
      return false;
    }
  }

  async setIsInterested(book: WritableSignal<Book>, set: boolean): Promise<void> {
    if (this.isLoggedIn()) {
      let currentIsInterested = book().isInterested || [];
      if (set) {
        if (!currentIsInterested.includes(this.me()!)) {
          currentIsInterested.push(this.me()!);
        }
      } else {
        currentIsInterested = currentIsInterested.filter((reader) => reader !== this.me()!);
      }
      if (currentIsInterested.length === 0) {
        currentIsInterested = [];
      }
      const data = {
        amazon_id: book().amazon_id,
        isInterested: JSON.stringify(currentIsInterested),
      }
      const book$ = this.http
        .put(`${this.bookApiUrl}/books/${book().amazon_id}/is_interested`, data, {
          headers: this.authHeaders()
        });
      const result = await firstValueFrom(book$) as Book;
      this.logService.info(JSON.stringify(result));
    }
  }
}
